<template>
  <div>
    <q-dialog
      v-model="transactions_settings_dialog_show"
      transition-show="jump-down"
      transition-hide="jump-up"
      @hide="transactions_settings_close"
    >
      <q-card class="transactions-settings-dialog">
        <div class="transactions-settings-dialog__container">
          <div class="transactions-settings-dialog__sidebar">
            <div class="transactions-settings-dialog__sidebar-top">
              <q-btn
                icon="close"
                flat
                round
                dense
                @click="transactions_settings_dialog_show = false"
              />
            </div>
            <q-list
              class="transactions-settings-dialog__sidebar-list"
              dense
            >
              <q-item
                v-for="section in transactions_settings_sections"
                :key="section.id"
                clickable
                class="transactions-settings-dialog__sidebar-item"
                :active="transactions_settings_active_page === section.id"
                active-class="transactions-settings-dialog__sidebar-item--active"
                @click="transactions_settings_active_page = section.id"
              >
                <q-item-section avatar>
                  <q-icon
                    :name="section.icon"
                    size="18px"
                  />
                </q-item-section>
                <q-item-section>
                  <q-item-label>{{ section.label }}</q-item-label>
                </q-item-section>
              </q-item>
            </q-list>
          </div>
          <div class="transactions-settings-dialog__content">
            <div class="transactions-settings-dialog__section-header">
              <div class="transactions-settings-dialog__section-header-row">
                <div class="transactions-settings-dialog__section-title">
                  {{ transactions_settings_active_section?.label }}
                </div>
                <q-btn
                  v-if="transactions_settings_active_page === 'address_mappings'"
                  dense
                  outline
                  color="primary"
                  icon="las la-plus"
                  unelevated
                  no-caps
                  :label="$q.screen.gt.xs ? transactions_settings.i18n.address_mappings_add_action_label : ''"
                  class="transactions-settings-dialog__section-action"
                  @click="transactions_settings_address_mapping_dialog_open()"
                />
              </div>
              <div class="transactions-settings-dialog__divider" />
            </div>
            <q-scroll-area class="transactions-settings-dialog__scroll">
              <!-- Display options -->
              <div v-if="transactions_settings_active_page === 'display_options'">
                <div
                  v-for="option in transactions_settings_display_rows"
                  :key="option.key"
                  class="transactions-settings-row"
                >
                  <div class="transactions-settings-row__text">
                    <div class="transactions-settings-row__label">
                      {{ option.label }}
                    </div>
                    <div
                      v-if="option.description"
                      class="transactions-settings-row__description"
                    >
                      {{ option.description }}
                    </div>
                  </div>
                  <div class="transactions-settings-row__control">
                    <template v-if="option.type === 'toggle'">
                      <q-toggle
                        :model-value="transactions_settings.display_options[option.key]"
                        color="positive"
                        @update:model-value="(v) => transactions_settings_display_options_update({ [option.key]: v })"
                      />
                    </template>
                    <template v-else>
                      <q-btn
                        outline
                        dense
                        class="transactions-settings-row__dropdown"
                        no-caps
                      >
                        <span>{{ option.value }}</span>
                        <q-icon name="sym_o_expand_more" />
                      </q-btn>
                    </template>
                  </div>
                </div>

                <div class="transactions-settings-row">
                  <div class="transactions-settings-row__text">
                    <div class="transactions-settings-row__label">
                      {{ transactions_settings.i18n.display_options_setting_round_amounts }}
                    </div>
                    <div class="transactions-settings-row__description">
                      {{ transactions_settings.i18n.display_options_setting_round_amounts_info }}
                    </div>
                  </div>
                  <div class="transactions-settings-row__control">
                    <q-toggle
                      :model-value="transactions_settings.display_options.settings_tr_rounded_amounts"
                      color="positive"
                      @update:model-value="(v) => transactions_settings_display_options_update({ settings_tr_rounded_amounts: v })"
                    />
                  </div>
                </div>
              </div>

              <!-- Address Mappings -->
              <div
                v-else-if="transactions_settings_active_page === 'address_mappings'"
                class="transactions-settings-dialog__panel-stack"
              >
                <div class="transactions-settings-dialog__panel">
                  <div class="transactions-settings-dialog__panel-header">
                    <div>
                      <div class="text-secondary-caption">
                        {{ transactions_settings.i18n.address_mappings_title_info }}
                      </div>
                    </div>
                  </div>
                  <div class="transactions-settings-dialog__panel-body q-mt-md">
                    <q-input
                      v-model="transactions_settings_search"
                      dense
                      outlined
                      :placeholder="transactions_settings.i18n.address_mappings_search_placeholder"
                      class="transactions-settings-dialog__search q-mb-md"
                    >
                      <template #append>
                        <q-icon name="search" />
                      </template>
                    </q-input>

                    <div
                      v-if="transactions_settings_filtered_mappings.length === 0"
                      class="transactions-settings-dialog__empty"
                    >
                      <q-banner
                        class="bg-pastel-green q-px-md"
                        dense
                        style="width: fit-content; margin: auto"
                      >
                        <template #avatar>
                          <q-icon
                            name="las la-exclamation-circle"
                            size="md"
                          />
                        </template>
                        <div>
                          <template v-if="transactions_settings_search?.length > 0">
                            {{ transactions_settings.i18n.address_mappings_results_empty }} ({{ transactions_settings_search }})
                          </template>
                          <template v-else>
                            {{ transactions_settings.i18n.address_mappings_empty }}
                          </template>
                        </div>
                      </q-banner>
                    </div>

                    <q-list v-else>
                      <q-item
                        v-for="mapping in transactions_settings_filtered_mappings"
                        :key="mapping.id"
                        class="q-px-none"
                      >
                        <q-item-section>
                          <q-item-label class="text-bold">
                            {{ mapping.name || mapping.address }}
                          </q-item-label>
                          <q-item-label
                            caption
                            class="transactions-settings-dialog__mapping-address"
                          >
                            <span
                              v-if="mapping.address"
                              class="transactions-settings-dialog__mapping-address-text"
                            >
                              {{ transactions_settings_truncate_address(mapping.address) }}
                              <q-tooltip>
                                {{ mapping.address }}
                              </q-tooltip>
                            </span>
                            <ClipboardCopy
                              v-if="mapping.address"
                              :content="mapping.address"
                              class="transactions-settings-dialog__mapping-copy"
                            >
                              <q-icon
                                name="las la-copy"
                                size="16px"
                                class="text-primary"
                              />
                            </ClipboardCopy>
                          </q-item-label>
                          <q-item-label
                            v-if="mapping.auxiliary_account_number"
                            caption
                          >
                            {{ transactions_settings.i18n.address_mappings_auxiliary_account_number_caption }} {{ mapping.auxiliary_account_number }}
                          </q-item-label>
                        </q-item-section>
                        <q-item-section
                          side
                          class="items-center"
                        >
                          <div class="flex items-center no-wrap">
                            <q-btn
                              color="primary"
                              round
                              flat
                              icon="las la-edit"
                              @click="transactions_settings_address_mapping_dialog_open(mapping)"
                            />
                            <q-btn
                              color="primary"
                              round
                              flat
                              icon="las la-trash"
                              class="q-ml-xs"
                              @click="transactions_settings_address_mapping_delete_confirm(mapping)"
                            />
                          </div>
                        </q-item-section>
                      </q-item>
                    </q-list>
                  </div>
                </div>
              </div>

              <div
                v-else
                class="transactions-settings-dialog__panel-stack"
              >
                <div class="transactions-settings-dialog__panel">
                  <!-- Placeholder for future transactions settings sections -->
                </div>
              </div>
            </q-scroll-area>
          </div>
        </div>
      </q-card>
    </q-dialog>

    <q-dialog
      v-model="transactions_settings_address_mappings_dialog_show"
      persistent
    >
      <q-card class="transactions-settings-dialog__form q-pa-lg">
        <q-card-section class="row items-start q-pb-none">
          <div class="col">
            <div class="text-h6">
              {{ transactions_settings_address_mappings_dialog_title }}
            </div>
            <div class="text-body2 text-muted">
              {{ transactions_settings_address_mappings_dialog_text }}
            </div>
          </div>
          <q-btn
            v-close-popup
            icon="close"
            flat
            round
            dense
          />
        </q-card-section>
        <q-card-section class="q-pt-md">
          <q-form @submit.prevent="transactions_settings_address_mapping_submit">
            <div class="q-mb-md">
              <span class="text-input-label">{{ transactions_settings.i18n.address_mappings_form_address }}</span>
              <div
                v-if="transactions_settings_address_mappings_is_edit"
                class="q-mt-sm text-body1"
              >
                {{ transactions_settings_address_mappings_form.address }}
              </div>
              <q-input
                v-else
                v-model="transactions_settings_address_mappings_form.address"
                autofocus
                dense
                outlined
                class="full-width q-mt-sm"
                :rules="[(val) => val?.trim() !== '' || transactions_settings.i18n.address_mappings_form_required]"
              />
            </div>
            <div class="q-mb-md">
              <span class="text-input-label">{{ transactions_settings.i18n.address_mappings_form_name }}</span>
              <q-input
                v-model="transactions_settings_address_mappings_form.name"
                outlined
                dense
                class="full-width q-mt-sm"
                :rules="[(val) => val?.trim() !== '' || transactions_settings.i18n.address_mappings_form_required]"
              />
            </div>
            <div class="q-mb-md">
              <span class="text-input-label">{{ transactions_settings.i18n.address_mappings_form_number }}</span>
              <q-input
                v-model="transactions_settings_address_mappings_form.auxiliary_account_number"
                outlined
                dense
                class="full-width q-mt-sm"
              />
            </div>
            <div class="text-right q-mt-lg">
              <q-btn
                flat
                :label="$q.lang.label.cancel"
                color="primary"
                class="q-mr-md"
                @click="transactions_settings_address_mappings_dialog_show = false"
              />
              <q-btn
                type="submit"
                color="primary"
                unelevated
                :label="transactions_settings_address_mappings_submit_label"
                :loading="transactions_settings_address_mappings_loading"
              />
            </div>
          </q-form>
        </q-card-section>
      </q-card>
    </q-dialog>
  </div>
</template>

<script>
import ClipboardCopy from '@/shared/clipboard_copy.vue';

export default {
  name: 'TransactionsSettingsDialog',
  components: {
    ClipboardCopy,
  },
  props: {
    transactions_settings: {
      type: Object,
      required: true,
    },
    transactions_settings_state: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      transactions_settings_dialog_show: this.transactions_settings_state?.open || false,
      transactions_settings_active_page: this.transactions_settings_state?.active_page || "display_options",
      transactions_settings_search: '',
      transactions_settings_address_mappings_dialog_show: false,
      transactions_settings_address_mappings_dialog_title: this.transactions_settings.i18n.address_mappings_form_title_add,
      transactions_settings_address_mappings_dialog_text: this.transactions_settings.i18n.address_mappings_form_text_add,
      transactions_settings_address_mappings_submit_label: this.$q.lang.label.create,
      transactions_settings_address_mappings_is_edit: false,
      transactions_settings_address_mappings_form: {
        id: null,
        address: '',
        name: '',
        auxiliary_account_number: '',
      },
      transactions_settings_address_mappings_loading: false,
    };
  },
  computed: {
    transactions_settings_sections() {
      return [
        {
          id: "display_options",
          label: this.transactions_settings.i18n.tabs_title_1,
          icon: "sym_o_tune",
        },
        {
          id: "address_mappings",
          label: this.transactions_settings.i18n.tabs_title_2,
          icon: "las la-address-book",
        },
      ];
    },
    transactions_settings_display_rows() {
      return [
        {
          key: "settings_tr_counter_value_col",
          label: this.transactions_settings.i18n.display_options_setting_counter_value,
          description: this.transactions_settings.i18n.display_options_setting_counter_value_info,
          type: "toggle",
        },
        {
          key: "settings_tr_wac_col",
          label: this.transactions_settings.i18n.display_options_setting_wac_display,
          description: this.transactions_settings.i18n.display_options_setting_wac_display_info,
          type: "toggle",
        },
        {
          key: "settings_tr_balance_col",
          label: this.transactions_settings.i18n.display_options_setting_balance_display,
          description: this.transactions_settings.i18n.display_options_setting_balance_display_info,
          type: "toggle",
        },
        {
          key: "settings_tr_import_source_col",
          label: this.transactions_settings.i18n.display_options_setting_import_source_display,
          description: this.transactions_settings.i18n.display_options_setting_import_source_display_info,
          type: "toggle",
        },
        {
          key: "settings_tr_info_col",
          label: this.transactions_settings.i18n.display_options_setting_note_and_attachments_display,
          description: this.transactions_settings.i18n.display_options_setting_note_and_attachments_display_info,
          type: "toggle",
        },
      ];
    },
    transactions_settings_filtered_mappings() {
      const mappings = this.transactions_settings.address_mappings || [];
      const search = this.transactions_settings_search?.toLowerCase().trim();
      if (!search) {
        return mappings;
      }
      return mappings.filter((mapping) => {
        return [
          mapping.name,
          mapping.address,
          mapping.auxiliary_account_number,
        ].filter((value) => value !== undefined && value !== null && value !== '').some((value) => {
          return value.toString().toLowerCase().includes(search);
        });
      });
    },
    transactions_settings_active_section() {
      return this.transactions_settings_sections.find((section) => section.id === this.transactions_settings_active_page) || this.transactions_settings_sections[0];
    },
  },
  watch: {
    transactions_settings_state: {
      handler(new_value) {
        if (new_value?.open) {
          this.transactions_settings_active_page = new_value.active_page || this.transactions_settings_active_page;
          this.transactions_settings_dialog_show = true;
          this.transactions_settings_update_url(this.transactions_settings_active_page);
        }
      },
      deep: true,
    },
    transactions_settings_active_page(new_value, old_value) {
      if (new_value !== old_value && this.transactions_settings_dialog_show) {
        this.transactions_settings_update_url(new_value);
      }
    },
  },
  methods: {
    transactions_settings_update_url: function (section, open = true) {
      if (typeof window === 'undefined') {
        return;
      }
      const url = new URL(window.location.href);
      if (open) {
        url.searchParams.set('settings_dialog', 'true');
        url.searchParams.set('settings_page', section || 'display_options');
      } else {
        url.searchParams.delete('settings_page');
        url.searchParams.delete('settings_dialog');
      }
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    },
    transactions_settings_open: function (section) {
      if (section) {
        this.transactions_settings_active_page = section;
      }
      if (!this.transactions_settings_active_page) {
        this.transactions_settings_active_page = "display_options";
      }
      this.transactions_settings_dialog_show = true;
      this.transactions_settings_update_url(this.transactions_settings_active_page);
    },
    transactions_settings_close: function () {
      this.transactions_settings_address_mappings_dialog_show = false;
      this.transactions_settings_clear_url_params();
    },
    transactions_settings_clear_url_params: function () {
      this.transactions_settings_update_url(null, false);
    },
    transactions_settings_display_options_update: function (params) {
      this.$inertia.post(
        this.transactions_settings.display_options_action_update,
        params,
        {
          preserveState: true,
          preserveScroll: true,
          replace: true,
          only: [
            'flash',
            'transactions_settings',
            'transactions_filters',
            'workspace_settings',
            'transactions'
          ],
          onSuccess: () => {
            this.$q.notify({
              message: this.$page.props.flash.success,
              type: "positive",
            });
          },
          onError: () => {
            this.$q.notify({
              message: this.$page.props.flash.alert,
              type: "negative",
            });
          },
        }
      );
    },
    transactions_settings_address_mapping_dialog_open: function (mapping = {}) {
      const hasId = Boolean(mapping?.id);
      this.transactions_settings_address_mappings_is_edit = hasId;
      this.transactions_settings_address_mappings_dialog_title = hasId
        ? this.transactions_settings.i18n.address_mappings_form_title_edit
        : this.transactions_settings.i18n.address_mappings_form_title_add;
      this.transactions_settings_address_mappings_dialog_text = hasId
        ? this.transactions_settings.i18n.address_mappings_form_text_edit
        : this.transactions_settings.i18n.address_mappings_form_text_add;
      this.transactions_settings_address_mappings_submit_label = hasId ? this.$q.lang.label.update : this.$q.lang.label.create;
      this.transactions_settings_address_mappings_form = structuredClone({
        id: mapping?.id || null,
        address: mapping?.address || '',
        name: mapping?.name || '',
        auxiliary_account_number: mapping?.auxiliary_account_number || '',
      });
      this.transactions_settings_address_mappings_dialog_show = true;
    },
    transactions_settings_address_mapping_submit: function () {
      this.$inertia.post(
        this.transactions_settings.address_mappings_action_update,
        this.transactions_settings_address_mappings_form,
        {
          preserveState: true,
          preserveScroll: true,
          replace: true,
          only: [
            'flash',
            'transactions_settings'
          ],
          onStart: () => {
            this.transactions_settings_address_mappings_loading = true;
          },
          onSuccess: () => {
            this.$q.notify({
              message: this.$page.props.flash.success,
              type: "positive",
            });
            this.transactions_settings_address_mappings_dialog_show = false;
          },
          onError: () => {
            this.$q.notify({
              message: this.$page.props.flash.alert,
              type: "negative",
            });
          },
          onFinish: () => {
            this.transactions_settings_address_mappings_loading = false;
          },
        }
      );
    },
    transactions_settings_address_mapping_delete_confirm: function (mapping) {
      this.$q.dialog({
        title: this.transactions_settings.i18n.address_mappings_delete_title,
        message: `${mapping.name || mapping.address} \n\n ${this.transactions_settings.i18n.address_mappings_delete_message}`,
        cancel: true,
        persistent: true,
        cardClass: "whitespace-pre-line",
      }).onOk(() => {
        this.transactions_settings_address_mapping_delete(mapping);
      });
    },
    transactions_settings_address_mapping_delete: function (mapping) {
      this.$inertia.post(
        this.transactions_settings.address_mappings_action_delete,
        { id: mapping.id },
        {
          preserveState: true,
          preserveScroll: true,
          replace: true,
          only: [
            'flash',
            'transactions_settings'
          ],
          onSuccess: () => {
            this.$q.notify({
              message: this.$page.props.flash.success,
              type: "positive",
            });
          },
          onError: () => {
            this.$q.notify({
              message: this.$page.props.flash.alert,
              type: "negative",
            });
          },
        }
      );
    },
    transactions_settings_truncate_address: function (address) {
      if (!address) {
        return '';
      }
      const normalized = String(address);
      if (normalized.length <= 16) {
        return normalized;
      }
      return `${normalized.slice(0, 8)}[...]${normalized.slice(-8)}`;
    },
  },
};
</script>

<style>
.transactions-settings-dialog {
  width: 100%;
  max-width: 720px;
  min-width: 720px;
  max-height: 85vh;
  border-radius: 16px !important;
  background-color: #101113;
  color: #f5f5f5;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

@media (min-width: 768px) {
  .transactions-settings-dialog {
    min-height: 650px;
  }
}

@media (max-width: 720px) {
  .transactions-settings-dialog {
    min-width: 100%;
    max-width: 100%;
  }
}

.body--light .transactions-settings-dialog {
  background-color: #ffffff;
  color: var(--text-default);
}

.transactions-settings-dialog__container {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

@media (min-width: 768px) {
  .transactions-settings-dialog__container {
    flex-direction: row;
  }
}

.transactions-settings-dialog__sidebar {
  display: flex;
  flex-direction: column;
  padding: 16px 8px;
  background: rgba(255, 255, 255, 0.04);
}

.transactions-settings-dialog__sidebar-top {
  margin-bottom: 12px;
}

@media (min-width: 768px) {
  .transactions-settings-dialog__sidebar {
    min-width: 240px;
    max-width: 260px;
  }
}

@media (max-width: 767px) {
  .transactions-settings-dialog__sidebar {
    min-width: 100%;
  }
}

.body--light .transactions-settings-dialog__sidebar {
  background: #f5f5f7;
}

.transactions-settings-dialog__sidebar-item {
  border-radius: 12px;
  margin-bottom: 4px;
}

.transactions-settings-dialog__sidebar-item--active {
  background: rgba(255, 255, 255, 0.12);
}

.body--light .transactions-settings-dialog__sidebar-item--active {
  background: rgba(0, 0, 0, 0.05);
}

.transactions-settings-dialog__content {
  flex: 1;
  padding: 24px;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

@media (max-width: 767px) {
  .transactions-settings-dialog__content {
    padding: 16px;
  }
}

.transactions-settings-dialog__section-header {
  margin-bottom: 12px;
}

.transactions-settings-dialog__section-header-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}

.transactions-settings-dialog__section-title {
  font-size: 18px;
  font-weight: 500;
  margin-bottom: 0;
}

.transactions-settings-dialog__section-action {
  margin-left: auto;
}

.transactions-settings-dialog__divider {
  width: 100%;
  height: 1px;
  background: rgba(255, 255, 255, 0.12);
}

.body--light .transactions-settings-dialog__divider {
  background: rgba(0, 0, 0, 0.1);
}

.transactions-settings-dialog__scroll {
  height: 100%;
  max-height: calc(85vh - 140px);
}

.transactions-settings-dialog__scroll .q-scrollarea__content {
  padding-right: 12px;
  /* keep scrollbar from covering content */
  box-sizing: border-box;
}

.transactions-settings-dialog__panel-stack {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.transactions-settings-dialog__panel-title {
  font-size: 18px;
  font-weight: 600;
}

.transactions-settings-dialog__panel-body {
  min-height: 120px;
}

.transactions-settings-dialog__mapping-address {
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}

.transactions-settings-dialog__mapping-address-text {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
}

.transactions-settings-dialog__mapping-copy {
  display: inline-flex;
}

.transactions-settings-dialog__mapping-copy .q-icon {
  cursor: pointer;
}

.transactions-settings-dialog__empty {
  padding: 40px 0;
}

.transactions-settings-dialog__form {
  width: 520px;
  max-width: 90vw;
}

.transactions-settings-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.transactions-settings-row:last-of-type {
  border-bottom: none;
}

.body--light .transactions-settings-row {
  border-bottom-color: rgba(0, 0, 0, 0.06);
}

.transactions-settings-row__text {
  max-width: 70%;
}

.transactions-settings-row__label {
  font-size: 15px;
  font-weight: 500;
}

.transactions-settings-row__description {
  font-size: 13px;
  opacity: 0.7;
  margin-top: 4px;
}

.transactions-settings-row__control {
  display: flex;
  align-items: center;
  gap: 10px;
}

.transactions-settings-row__dropdown {
  border-radius: 999px;
  padding: 0 16px;
  min-height: 36px;
}

@media (max-width: 767px) {
  .transactions-settings-dialog__sidebar-list {
    display: flex;
    gap: 8px;
    overflow-x: auto;
  }

  .transactions-settings-dialog__sidebar-list .q-item {
    flex: 1;
    margin-bottom: 0;
  }
}
</style>
